import {
    AuditoriaAccion,
    AuditoriaAudiencia,
    AuditoriaModulo,
    AuditoriaPantalla,
} from '@enum/auditoria/auditoria-keys.enum';
import { SignoAuditoriaEvento } from '@interfaces/interface-bakend/auditoria/signo-auditoria-evento.interface';

export interface AuditoriaAccionMeta {
    label: string;
    verbo: string;
    icono: string;
}

export interface AuditoriaComponenteMeta {
    modulo: AuditoriaModulo;
    titulo: string;
    descripcion: string;
    audiencia: AuditoriaAudiencia;
    icono: string;
    ruta: string;
}

export const AUDITORIA_ACCIONES: Record<string, AuditoriaAccionMeta> = {
    [AuditoriaAccion.PANTALLA_ACCESO]: { label: 'Acceso a pantalla', verbo: 'accedió a', icono: 'login' },
    [AuditoriaAccion.VISUALIZAR]: { label: 'Visualización', verbo: 'visualizó', icono: 'visibility' },
    [AuditoriaAccion.CREAR_CON_PDF]: { label: 'Creación (con PDF)', verbo: 'creó (con PDF)', icono: 'note_add' },
    [AuditoriaAccion.CREAR_SIN_PDF]: { label: 'Creación (sin PDF)', verbo: 'creó (sin PDF)', icono: 'add' },
    [AuditoriaAccion.EDITAR_CON_PDF]: { label: 'Edición (con PDF)', verbo: 'editó (con PDF)', icono: 'edit_note' },
    [AuditoriaAccion.EDITAR_SIN_PDF]: { label: 'Edición (sin PDF)', verbo: 'editó (sin PDF)', icono: 'edit' },
};

export const AUDITORIA_MODULOS: Record<string, { label: string; icono: string }> = {
    [AuditoriaModulo.COSEGUROS]: { label: 'Coseguros', icono: 'medical_services' },
    [AuditoriaModulo.PLANES]: { label: 'Planes', icono: 'assignment' },
    [AuditoriaModulo.INSCRIPCION_OS]: { label: 'Inscripciones', icono: 'how_to_reg' },
};

export const AUDITORIA_PANTALLAS: Record<string, string> = {
    [AuditoriaPantalla.COSEGUROS]: 'Coseguros',
    [AuditoriaPantalla.PLAN_ALTA]: 'Alta de Plan',
    [AuditoriaPantalla.PLAN_MODIFICACION]: 'Modificación de Plan',
    [AuditoriaPantalla.INSCRIPCION_OS]: 'Inscripción O.S.',
};

/**
 * Registro expandible de los componentes auditables.
 * Agregar un nuevo dominio a auditar = agregar una entrada acá.
 */
export const AUDITORIA_COMPONENTES: AuditoriaComponenteMeta[] = [
    {
        modulo: AuditoriaModulo.COSEGUROS,
        titulo: 'Coseguros',
        descripcion: 'Accesos y consultas de coseguros realizadas por los afiliados.',
        audiencia: AuditoriaAudiencia.AFILIADOS,
        icono: 'medical_services',
        ruta: '/auditoria-listado',
    },
    {
        modulo: AuditoriaModulo.PLANES,
        titulo: 'Planes',
        descripcion: 'Altas, modificaciones y accesos sobre planes por la administración.',
        audiencia: AuditoriaAudiencia.ADMINISTRADORES,
        icono: 'assignment',
        ruta: '/auditoria-listado',
    },
    {
        modulo: AuditoriaModulo.INSCRIPCION_OS,
        titulo: 'Inscripciones',
        descripcion: 'Trazabilidad del flujo público de inscripción: registro, subida de documentos y declaración jurada.',
        audiencia: AuditoriaAudiencia.AFILIADOS,
        icono: 'how_to_reg',
        ruta: '/auditoria-inscripciones',
    },
];

export function labelAccion(accion?: string): string {
    return (accion && AUDITORIA_ACCIONES[accion]?.label) || accion || 'Acción desconocida';
}

export function iconoAccion(accion?: string): string {
    return (accion && AUDITORIA_ACCIONES[accion]?.icono) || 'help_outline';
}

export function labelModulo(modulo?: string): string {
    return (modulo && AUDITORIA_MODULOS[modulo]?.label) || modulo || '-';
}

export function labelPantalla(pantalla?: string): string {
    return (pantalla && AUDITORIA_PANTALLAS[pantalla]) || pantalla || '-';
}

/**
 * Arma una frase legible (vista administrador) a partir de un evento crudo.
 * Ej: "Naumann Thostrup, Ivan accedió a Modificación de Plan (Plan 100)"
 */
export function describirEvento(evento: SignoAuditoriaEvento): string {
    const usuario = evento.usuario?.nombreCompleto || 'Usuario desconocido';
    const verbo = (evento.accion && AUDITORIA_ACCIONES[evento.accion]?.verbo) || 'realizó una acción en';
    const donde = labelPantalla(evento.pantalla) !== '-' ? labelPantalla(evento.pantalla) : labelModulo(evento.modulo);
    const detalle = resumenPayload(evento);
    return `${usuario} ${verbo} ${donde}${detalle ? ` (${detalle})` : ''}`;
}

/**
 * Extrae un resumen corto y legible del payload para la grilla.
 */
export function resumenPayload(evento: SignoAuditoriaEvento): string {
    const p = evento.payload || {};
    const partes: string[] = [];
    if (p.denominacion) { partes.push(`${p.denominacion}`); }
    if (p.planDenominacion) { partes.push(`${p.planDenominacion}`); }
    if (p.pdfFilename) { partes.push(`PDF: ${p.pdfFilename}`); }
    if (p.tienePdf !== undefined && !p.pdfFilename) { partes.push(p.tienePdf ? 'con PDF' : 'sin PDF'); }
    if (p.sinDatosGecros) { partes.push('sin datos GECROS'); }
    return partes.join(' · ');
}
